import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { ContentPage, ContentSection, BiText } from "@/components/layout/ContentPage";
import { Button } from "@/components/ui/button";
import { buildSeo } from "@/lib/seo";
import { useT } from "@/lib/i18n";

export const Route = createFileRoute("/contact")({
  head: () => {
    const seo = buildSeo({
      title: "Contact Us | Bangla Calculator",
      description:
        "Contact Bangla Calculator. Suggestions, bug reports, calculator requests, religious content feedback all welcome.",
      path: "/contact",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Contact", path: "/contact" },
      ],
    });
    return { meta: seo.meta, links: seo.links, scripts: seo.scripts };
  },
  component: ContactPage,
});

const SUBJECTS = [
  { value: "suggestion", en: "Suggestion", bn: "পরামর্শ" },
  { value: "bug", en: "Bug report", bn: "বাগ রিপোর্ট" },
  { value: "request", en: "Calculator request", bn: "নতুন ক্যালকুলেটরের অনুরোধ" },
  { value: "religious", en: "Religious content feedback", bn: "ধর্মীয় কনটেন্ট ফিডব্যাক" },
  { value: "general", en: "General feedback", bn: "সাধারণ ফিডব্যাক" },
  { value: "other", en: "Other", bn: "অন্যান্য" },
] as const;

const schema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().email().max(255),
  subject: z.enum(SUBJECTS.map((s) => s.value) as [string, ...string[]]),
  message: z.string().trim().min(5).max(2000),
  // honeypot
  website: z.string().max(0).optional(),
});

function ContactPage() {
  const { lang } = useT();
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    const form = e.currentTarget;
    const data = Object.fromEntries(new FormData(form).entries());
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      setError(lang === "bn" ? "সব ফিল্ড সঠিকভাবে পূরণ করুন।" : "Please fill all fields correctly.");
      return;
    }
    if (parsed.data.website) {
      setStatus("success");
      form.reset();
      return;
    }
    setStatus("submitting");
    try {
      // Mailto fallback (no backend in V1). Replace with Formspree/Web3Forms endpoint later.
      const subjectLabel = SUBJECTS.find((s) => s.value === parsed.data.subject)?.en ?? "Feedback";
      const target =
        parsed.data.subject === "religious"
          ? "banglacalculatorbd@gmail.com"
          : "banglacalculatorbd@gmail.com";
      const body = `Name: ${parsed.data.name}\nEmail: ${parsed.data.email}\n\n${parsed.data.message}`;
      window.location.href = `mailto:${target}?subject=${encodeURIComponent(`[${subjectLabel}] Bangla Calculator`)}&body=${encodeURIComponent(body)}`;
      setStatus("success");
      form.reset();
    } catch {
      setStatus("error");
      setError(lang === "bn" ? "পাঠাতে সমস্যা হয়েছে।" : "Couldn't send the message.");
    }
  }

  return (
    <ContentPage
      titleEn="Contact Us"
      titleBn="যোগাযোগ"
      introEn="We'd love to hear from you. Suggestions, bug reports, calculator requests, or feedback — all welcome."
      introBn="আপনার মতামত শোনার জন্য আমরা অপেক্ষমাণ। পরামর্শ, বাগ রিপোর্ট, নতুন ক্যালকুলেটরের অনুরোধ বা ফিডব্যাক — সব স্বাগত।"
    >
      <ContentSection headingEn="Send a message" headingBn="বার্তা পাঠান">
        <form onSubmit={onSubmit} className="space-y-4 rounded-xl border border-border bg-card p-4 sm:p-5" noValidate>
          {/* honeypot */}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden />

          <Field labelEn="Name" labelBn="নাম" required>
            <input
              name="name"
              required
              maxLength={100}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-accent"
            />
          </Field>

          <Field labelEn="Email" labelBn="ইমেইল" required>
            <input
              type="email"
              name="email"
              required
              maxLength={255}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-accent"
            />
          </Field>

          <Field labelEn="Subject" labelBn="বিষয়" required>
            <select
              name="subject"
              required
              defaultValue="suggestion"
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-accent"
            >
              {SUBJECTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {lang === "bn" ? s.bn : s.en}
                </option>
              ))}
            </select>
          </Field>

          <Field labelEn="Message" labelBn="বার্তা" required>
            <textarea
              name="message"
              required
              minLength={5}
              maxLength={2000}
              rows={6}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm outline-none focus:border-accent"
            />
          </Field>

          {error && <p className="text-sm text-destructive">{error}</p>}
          {status === "success" && (
            <p className="text-sm text-green-600 dark:text-green-400">
              <BiText en="Thanks — your email client should open. If not, write to banglacalculatorbd@gmail.com." bn="ধন্যবাদ — আপনার ইমেইল ক্লায়েন্ট খুলবে। না খুললে banglacalculatorbd@gmail.com-এ লিখুন।" />
            </p>
          )}

          <Button type="submit" disabled={status === "submitting"} className="w-full sm:w-auto">
            <BiText en={status === "submitting" ? "Sending…" : "Send message"} bn={status === "submitting" ? "পাঠানো হচ্ছে…" : "পাঠান"} />
          </Button>
        </form>

        <p className="mt-3 text-xs text-muted-foreground">
          <BiText
            en={<>Or email us directly: <a className="text-accent underline underline-offset-2" href="mailto:banglacalculatorbd@gmail.com">banglacalculatorbd@gmail.com</a></>}
            bn={<>অথবা সরাসরি ইমেইল করুন: <a className="text-accent underline underline-offset-2" href="mailto:banglacalculatorbd@gmail.com">banglacalculatorbd@gmail.com</a></>}
          />
        </p>
      </ContentSection>

      <ContentSection headingEn="Religious content feedback" headingBn="ধর্মীয় কনটেন্ট ফিডব্যাক">
        <BiText
          en={
            <p>
              Notice an error in Islamic calculations or content? Write to{" "}
              <a className="text-accent underline underline-offset-2" href="mailto:banglacalculatorbd@gmail.com">banglacalculatorbd@gmail.com</a>.
              We treat religious accuracy seriously. Verified scholarly corrections are reviewed within 48 hours.
            </p>
          }
          bn={
            <p>
              ইসলামিক ক্যালকুলেশন বা কনটেন্টে কোনো ভুল লক্ষ্য করেছেন?{" "}
              <a className="text-accent underline underline-offset-2" href="mailto:banglacalculatorbd@gmail.com">banglacalculatorbd@gmail.com</a>-এ লিখুন।
              ধর্মীয় নির্ভুলতাকে আমরা গুরুত্বের সাথে দেখি। যাচাইকৃত আলেমদের সংশোধন ৪৮ ঘণ্টার মধ্যে পর্যালোচিত হয়।
            </p>
          }
        />
      </ContentSection>

      <ContentSection headingEn="Suggest a calculator" headingBn="নতুন ক্যালকুলেটরের প্রস্তাব">
        <BiText
          en={<p>Missing a tool you need? Tell us — user requests directly shape what we build next. Use the form above with subject "Calculator request".</p>}
          bn={<p>কোনো প্রয়োজনীয় টুল নেই? আমাদের জানান — ব্যবহারকারীর অনুরোধই আমরা পরবর্তীতে কী তৈরি করব তা ঠিক করে। উপরের ফর্মে "নতুন ক্যালকুলেটরের অনুরোধ" বিষয় বেছে নিন।</p>}
        />
      </ContentSection>
    </ContentPage>
  );
}

function Field({ labelEn, labelBn, required, children }: { labelEn: string; labelBn: string; required?: boolean; children: React.ReactNode }) {
  const { lang } = useT();
  return (
    <label className="block">
      <span className={`mb-1 block text-sm font-medium ${lang === "bn" ? "font-bn" : ""}`}>
        {lang === "bn" ? labelBn : labelEn}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </span>
      {children}
    </label>
  );
}
